import { Injectable } from '@angular/core';

const TRUST_KEY_PREFIX = 'presidio-mfa-trust:';
const PENDING_LOGIN_KEY = 'presidio-mfa-pending-login';
const PENDING_TRUST_KEY = 'presidio-mfa-pending-trust';
const TRUST_DAYS = 90;

/**
 * DEMO / PROVISIONAL — remember MFA-trusted devices for 90 days (FR 1.4.7 UX).
 * Production ICM should issue a trusted-device cookie/token; this localStorage bridge
 * lets the Presidio mock skip OTP when trust is still valid.
 */
@Injectable({ providedIn: 'root' })
export class MfaDeviceTrustService {
  setPendingLogin(login: string): void {
    if (SSR || !login) {
      return;
    }
    try {
      sessionStorage.setItem(PENDING_LOGIN_KEY, login.trim().toLowerCase());
    } catch {
      /* ignore */
    }
  }

  getPendingLogin(): string | undefined {
    if (SSR) {
      return undefined;
    }
    try {
      return sessionStorage.getItem(PENDING_LOGIN_KEY) || undefined;
    } catch {
      return undefined;
    }
  }

  /** Call when the user checks "Trust this device" and submits a valid OTP. */
  requestTrustOnSuccess(): void {
    if (SSR) {
      return;
    }
    try {
      sessionStorage.setItem(PENDING_TRUST_KEY, '1');
    } catch {
      /* ignore */
    }
  }

  clearTrustRequest(): void {
    if (SSR) {
      return;
    }
    try {
      sessionStorage.removeItem(PENDING_TRUST_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Persist 90-day trust after a successful MFA login when requested. */
  commitPendingTrust(): void {
    if (SSR) {
      return;
    }
    try {
      if (sessionStorage.getItem(PENDING_TRUST_KEY) !== '1') {
        return;
      }
      const login = this.getPendingLogin();
      if (!login) {
        return;
      }
      const until = new Date(Date.now() + TRUST_DAYS * 24 * 60 * 60 * 1000).toISOString();
      localStorage.setItem(`${TRUST_KEY_PREFIX}${login}`, until);
    } catch {
      /* ignore */
    } finally {
      this.clearTrustRequest();
    }
  }

  isTrusted(login: string): boolean {
    if (SSR || !login) {
      return false;
    }
    try {
      const until = localStorage.getItem(`${TRUST_KEY_PREFIX}${login.trim().toLowerCase()}`);
      if (!until) {
        return false;
      }
      return Date.parse(until) > Date.now();
    } catch {
      return false;
    }
  }

  /** Same key the mock interceptor reads (DEMO-ONLY). */
  static storageKeyFor(login: string): string {
    return `${TRUST_KEY_PREFIX}${login.trim().toLowerCase()}`;
  }
}
